import React, { useEffect, useState } from 'react';
import { Send, Users, User, Megaphone, Flame, Mic, Image, Link, Plus, X } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import VoiceRecorder from '../../components/VoiceRecorder';
import { getImageUrl } from '../../utils/imageUrl';

const AdminBroadcasts = () => {
  const [broadcasts, setBroadcasts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    voice_url: null,
    image_url: '',
    images: [],
    links: [],
    is_hot_news: false,
    target: 'all'
  });
  const [showVoice, setShowVoice] = useState(false);
  const [showMedia, setShowMedia] = useState(false);
  const [newImage, setNewImage] = useState('');
  const [newLink, setNewLink] = useState({ name: '', url: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [broadcastRes, usersRes] = await Promise.all([
        api.get('/admin/broadcasts'),
        api.get('/admin/users')
      ]);
      setBroadcasts(broadcastRes.data);
      // Handle both array and object with users property
      const userData = Array.isArray(usersRes.data) ? usersRes.data : usersRes.data.users || [];
      setUsers(userData);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.message) {
      toast.error('Please fill in title and message');
      return;
    }

    try {
      await api.post('/admin/broadcasts', formData);
      toast.success('Broadcast sent successfully!');
      setFormData({ title: '', message: '', voice_url: null, image_url: '', images: [], links: [], is_hot_news: false, target: 'all' });
      setNewImage('');
      setNewLink({ name: '', url: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to send broadcast');
    }
  };

  const handleVoiceRecorded = (voiceUrl) => {
    setFormData({ ...formData, voice_url: voiceUrl });
    toast.success('Voice message attached');
  };

  const addImage = () => {
    if (newImage.trim()) {
      setFormData({ ...formData, images: [...formData.images, newImage.trim()] });
      setNewImage('');
    }
  };

  const removeImage = (index) => {
    setFormData({ ...formData, images: formData.images.filter((_, i) => i !== index) });
  };

  const addLink = () => {
    if (newLink.name.trim() && newLink.url.trim()) {
      setFormData({ ...formData, links: [...formData.links, { ...newLink }] });
      setNewLink({ name: '', url: '' });
    }
  };

  const removeLink = (index) => {
    setFormData({ ...formData, links: formData.links.filter((_, i) => i !== index) });
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="admin-broadcasts-page">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl font-bold mb-8 flex items-center gap-3">
          <Megaphone className="w-10 h-10 text-primary" />
          Broadcast Center
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Create Broadcast Form */}
          <div className="glass-card p-6">
            <h2 className="text-2xl font-semibold mb-6">Send New Broadcast</h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm mb-1">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                  placeholder="Announcement title..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm mb-1">Message *</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl resize-none"
                  rows={4}
                  placeholder="Your message to members..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm mb-1">Target Audience</label>
                <select
                  value={formData.target}
                  onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                >
                  <option value="all">All Members</option>
                  {users.filter(u => u.role !== 'admin').map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name} (@{user.username})
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_hot_news}
                    onChange={(e) => setFormData({ ...formData, is_hot_news: e.target.checked })}
                    className="w-5 h-5"
                  />
                  <Flame className="w-5 h-5 text-orange-500" />
                  <span>Mark as Hot News</span>
                </label>
              </div>

              {/* Media Section - Images & Links */}
              <div>
                <button
                  type="button"
                  onClick={() => setShowMedia(!showMedia)}
                  className="flex items-center gap-2 text-green-500 hover:underline mb-3"
                >
                  <Image className="w-5 h-5" />
                  {showMedia ? 'Hide Media Options' : 'Add Images & Links'}
                </button>

                {showMedia && (
                  <div className="space-y-4 p-4 bg-white/5 rounded-xl">
                    {/* Main Image URL */}
                    <div>
                      <label className="block text-sm mb-1">Main Image URL</label>
                      <input
                        type="url"
                        value={formData.image_url}
                        onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                        placeholder="https://example.com/image.jpg"
                      />
                    </div>

                    {/* Additional Images */}
                    <div>
                      <label className="block text-sm mb-1">Additional Images</label>
                      <div className="flex gap-2 mb-2">
                        <input
                          type="url"
                          value={newImage}
                          onChange={(e) => setNewImage(e.target.value)}
                          className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                          placeholder="Image URL"
                        />
                        <button type="button" onClick={addImage} className="px-3 py-2 bg-green-500/20 text-green-500 rounded-lg">
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      {formData.images.map((img, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-white/10 px-3 py-2 rounded-lg mb-1">
                          <Image className="w-4 h-4 text-green-500" />
                          <span className="flex-1 text-sm truncate">{img}</span>
                          <button type="button" onClick={() => removeImage(idx)} className="text-red-500">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>

                    {/* Links */}
                    <div>
                      <label className="block text-sm mb-1">Links</label>
                      <div className="flex gap-2 mb-2">
                        <input
                          type="text"
                          value={newLink.name}
                          onChange={(e) => setNewLink({ ...newLink, name: e.target.value })}
                          className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                          placeholder="Link name"
                        />
                        <input
                          type="url"
                          value={newLink.url}
                          onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                          className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                          placeholder="URL"
                        />
                        <button type="button" onClick={addLink} className="px-3 py-2 bg-blue-500/20 text-blue-500 rounded-lg">
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      {formData.links.map((link, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-white/10 px-3 py-2 rounded-lg mb-1">
                          <Link className="w-4 h-4 text-blue-500" />
                          <span className="flex-1 text-sm">{link.name}</span>
                          <button type="button" onClick={() => removeLink(idx)} className="text-red-500">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Voice Recording */}
              <div>
                <button
                  type="button"
                  onClick={() => setShowVoice(!showVoice)}
                  className="flex items-center gap-2 text-primary hover:underline"
                >
                  <Mic className="w-5 h-5" />
                  {showVoice ? 'Hide Voice Recorder' : 'Add Voice Message'}
                </button>
                
                {showVoice && (
                  <div className="mt-3">
                    <VoiceRecorder 
                      onVoiceRecorded={handleVoiceRecorded}
                      onTextTranscribed={(text) => setFormData({ ...formData, message: formData.message + ' ' + text })}
                    />
                  </div>
                )}

                {formData.voice_url && (
                  <div className="mt-2 p-2 bg-primary/10 rounded-lg text-sm">
                    ✅ Voice message attached
                  </div>
                )}
              </div>

              <button
                type="submit"
                className="w-full btn-primary flex items-center justify-center gap-2"
                data-testid="send-broadcast-btn"
              >
                <Send className="w-5 h-5" />
                Send Broadcast
              </button>
            </form>
          </div>

          {/* Recent Broadcasts */}
          <div>
            <h2 className="text-2xl font-semibold mb-6">Recent Broadcasts</h2>
            
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : broadcasts.length === 0 ? (
              <div className="glass-card p-6 text-center text-muted-foreground">
                No broadcasts sent yet
              </div>
            ) : (
              <div className="space-y-4 max-h-[600px] overflow-y-auto">
                {broadcasts.map((b) => (
                  <div key={b.id} className="glass-card p-4" data-testid={`broadcast-${b.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {b.is_hot_news && <Flame className="w-5 h-5 text-orange-500" />}
                        <h3 className="font-semibold">{b.title}</h3>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        {b.target === 'all' ? (
                          <><Users className="w-4 h-4" /> All</>
                        ) : (
                          <><User className="w-4 h-4" /> Specific</>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">{b.message}</p>
                    
                    {/* Display Images */}
                    {(b.image_url || (b.images && b.images.length > 0)) && (
                      <div className="flex gap-2 flex-wrap mb-2">
                        {b.image_url && (
                          <img src={getImageUrl(b.image_url)} alt="broadcast" className="w-20 h-20 object-cover rounded-lg" />
                        )}
                        {b.images?.map((img, idx) => (
                          <img key={idx} src={getImageUrl(img)} alt={`broadcast-${idx}`} className="w-20 h-20 object-cover rounded-lg" />
                        ))}
                      </div>
                    )}

                    {/* Display Links */}
                    {b.links && b.links.length > 0 && (
                      <div className="flex gap-2 flex-wrap mb-2">
                        {b.links.map((link, idx) => (
                          <a 
                            key={idx} 
                            href={link.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs bg-blue-500/20 text-blue-500 px-2 py-1 rounded flex items-center gap-1"
                          >
                            <Link className="w-3 h-3" /> {link.name}
                          </a>
                        ))}
                      </div>
                    )}

                    {b.voice_url && (
                      <audio controls className="w-full h-8 mt-2">
                        <source src={getImageUrl(b.voice_url)} />
                      </audio>
                    )}
                    <p className="text-xs text-muted-foreground mt-2">
                      {new Date(b.created_at).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminBroadcasts;
